import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Cloud,
  Copy,
  Database,
  Eye,
  EyeOff,
  Github,
  Gitlab,
  Globe,
  KeyRound,
  Mail,
  Pencil,
  Plus,
  Server,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useI18n } from '../../contexts/I18nContext';
import { useDeleteVaultEntry, useVaultAudit, useVaultEntries } from '../../hooks/useVault';
import { revealVaultEntry, VAULT_TYPES, type VaultEntry, type VaultType } from '../../lib/vaultApi';
import type { ProjectDetail } from '../../lib/projectsApi';
import { Select } from '../ui/Select';
import { Skeleton } from '../ui/Skeleton';
import { useConfirm } from '../ui/ConfirmProvider';
import { VaultEntryModal } from './VaultEntryModal';

// ---------------------------------------------------------------------------
// Vault tab — per-project credentials.
//
// Three RBAC levels, all enforced again server-side:
//   canViewVault   — this tab exists at all; secret values arrive as null
//   canRevealVault — the eye button; each click writes a REVEAL audit row
//   canEditVault   — add/edit/delete + the audit log panel
//
// A revealed secret re-masks itself after 30s so a shared screen or an
// unattended laptop doesn't leave a production password on display.
// ---------------------------------------------------------------------------

const REVEAL_TIMEOUT_MS = 30_000;

const TYPE_ICONS: Record<string, typeof Server> = {
  SERVER: Server,
  GITHUB: Github,
  GITLAB: Gitlab,
  AZURE: Cloud,
  AWS: Cloud,
  DATABASE: Database,
  EMAIL: Mail,
  FTP: Server,
  API: KeyRound,
  HOSTING: Globe,
  DOMAIN: Globe,
  ACCOUNT: UserIcon,
};

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

// ---------------------------------------------------------------------------
// A single field row inside a card.
// ---------------------------------------------------------------------------

function FieldRow({
  projectId,
  entry,
  label,
  secret,
  value,
  hasValue,
  canReveal,
}: {
  projectId: string;
  entry: VaultEntry;
  label: string;
  secret: boolean;
  value: string | null;
  hasValue: boolean;
  canReveal: boolean;
}) {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const handles = timers.current;
    return () => handles.forEach((h) => window.clearTimeout(h));
  }, []);

  const shown = secret ? revealed : value;

  async function toggleReveal() {
    if (revealed !== null) {
      setRevealed(null);
      return;
    }
    setError(null);
    try {
      const fields = await revealVaultEntry(projectId, entry.id, label);
      const found = fields.find((f) => f.label === label);
      setRevealed(found?.value ?? '');
      timers.current.push(window.setTimeout(() => setRevealed(null), REVEAL_TIMEOUT_MS));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vault.revealFailed'));
    }
  }

  async function copy() {
    setError(null);
    // Non-secret values are already in hand; a secret has to be fetched, which
    // means copying a password is audited exactly like revealing it.
    let text = shown;
    if (text === null || text === undefined) {
      if (!secret || !canReveal) return;
      try {
        const fields = await revealVaultEntry(projectId, entry.id, label);
        text = fields.find((f) => f.label === label)?.value ?? '';
      } catch (err) {
        setError(err instanceof Error ? err.message : t('vault.revealFailed'));
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      timers.current.push(window.setTimeout(() => setCopied(false), 2000));
    } catch {
      setError(t('vault.copyFailed'));
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-2 py-1.5">
      <span className="w-40 flex-none truncate text-sm text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 truncate font-mono text-sm text-gray-900">
        {shown !== null && shown !== undefined ? (
          shown || <span className="font-sans text-gray-400">—</span>
        ) : hasValue ? (
          '••••••••'
        ) : (
          <span className="font-sans text-gray-400">—</span>
        )}
      </span>
      {secret && canReveal && hasValue && (
        <button
          type="button"
          onClick={toggleReveal}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label={revealed !== null ? t('vault.hide') : t('vault.reveal')}
          title={revealed !== null ? t('vault.hide') : t('vault.reveal')}
        >
          {revealed !== null ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      )}
      {hasValue && (!secret || canReveal) && (
        <button
          type="button"
          onClick={copy}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label={t('vault.copy')}
          title={copied ? t('vault.copied') : t('vault.copy')}
        >
          {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
        </button>
      )}
      {error && <span className="w-full text-xs text-red-600">{error}</span>}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Entry card
// ---------------------------------------------------------------------------

function EntryCard({
  projectId,
  entry,
  canReveal,
  canEdit,
  onEdit,
  onDelete,
}: {
  projectId: string;
  entry: VaultEntry;
  canReveal: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const Icon = TYPE_ICONS[entry.type] ?? KeyRound;
  const hasSecret = entry.fields.some((f) => f.secret);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <Icon size={18} className="shrink-0 text-gray-500" />
          <h3 className="truncate font-medium text-gray-900">{entry.title}</h3>
          <span className="ui-badge">{t(`vault.type.${entry.type}`)}</span>
        </div>
        {canEdit && (
          <div className="flex flex-none items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label={t('vault.editEntry')}
            >
              <Pencil size={15} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
              aria-label={t('vault.deleteEntry')}
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </div>

      {entry.url && (
        <a
          href={entry.url}
          target="_blank"
          rel="noreferrer"
          className="mt-1 block truncate text-sm text-blue-600 underline"
        >
          {entry.url}
        </a>
      )}

      {entry.decryptError ? (
        <p className="mt-3 text-sm text-red-600">{t('vault.decryptError')}</p>
      ) : entry.fields.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">{t('vault.noFields')}</p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100">
          {entry.fields.map((field) => (
            <FieldRow
              key={field.label}
              projectId={projectId}
              entry={entry}
              label={field.label}
              secret={field.secret}
              value={field.value}
              hasValue={field.hasValue}
              canReveal={canReveal}
            />
          ))}
        </ul>
      )}

      {hasSecret && !canReveal && (
        <p className="mt-2 text-xs text-gray-400">{t('vault.noRevealPermission')}</p>
      )}

      {entry.notes && (
        <p className="mt-3 whitespace-pre-wrap text-sm text-gray-600">{entry.notes}</p>
      )}

      {entry.updatedBy && (
        <p className="mt-3 text-xs text-gray-400">
          {t('vault.updatedBy', { name: entry.updatedBy.name })} · {formatDateTime(entry.updatedAt)}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit panel
// ---------------------------------------------------------------------------

function AuditPanel({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useVaultAudit(projectId);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-9 rounded" />
        ))}
      </div>
    );
  }
  if (isError || !data) {
    return <p className="text-sm text-red-600">{t('vault.audit.loadFailed')}</p>;
  }
  if (data.length === 0) {
    return <p className="text-sm text-gray-500">{t('vault.audit.empty')}</p>;
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-2 pl-4 pr-4">{t('vault.audit.date')}</th>
            <th className="py-2 pr-4">{t('vault.audit.user')}</th>
            <th className="py-2 pr-4">{t('vault.audit.action')}</th>
            <th className="py-2 pr-4">{t('vault.audit.entry')}</th>
            <th className="py-2 pr-4">{t('vault.audit.details')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              <td className="whitespace-nowrap py-2 pl-4 pr-4 text-gray-700">
                {formatDateTime(row.createdAt)}
              </td>
              <td className="py-2 pr-4 text-gray-900">{row.userName}</td>
              <td className="py-2 pr-4 text-gray-700">{t(`vault.action.${row.action}`)}</td>
              <td className="py-2 pr-4 text-gray-700">{row.entryTitle}</td>
              <td className="py-2 pr-4 text-gray-500">{row.details ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------

export function VaultTab({ project }: { project: ProjectDetail }) {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canReveal = can('projects', 'canRevealVault');
  const canEdit = can('projects', 'canEditVault');

  const { data, isLoading, isError } = useVaultEntries(project.id);
  const deleteMutation = useDeleteVaultEntry(project.id);
  const confirm = useConfirm();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showAudit, setShowAudit] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingEntry, setEditingEntry] = useState<VaultEntry | null>(null);

  // Group by type so a project with a dozen credentials reads as a few short
  // sections instead of one long list. Server already sorts by type, then title.
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = (data ?? []).filter((entry) => {
      if (typeFilter && entry.type !== typeFilter) return false;
      if (!term) return true;
      return (
        entry.title.toLowerCase().includes(term) ||
        entry.fields.some((f) => f.label.toLowerCase().includes(term))
      );
    });
    const byType = new Map<string, VaultEntry[]>();
    for (const entry of filtered) {
      const bucket = byType.get(entry.type);
      if (bucket) bucket.push(entry);
      else byType.set(entry.type, [entry]);
    }
    return [...byType.entries()];
  }, [data, search, typeFilter]);

  async function handleDelete(entry: VaultEntry) {
    const confirmed = await confirm({
      message: t('vault.deleteConfirm', { title: entry.title }),
      danger: true,
    });
    if (!confirmed) return;
    deleteMutation.mutate(entry.id);
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-9 rounded" width={320} />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return <p className="text-sm text-red-600">{t('vault.loadFailed')}</p>;
  }

  return (
    <div>
      {/* Phones: search and type filter full width, the two actions as two
          equal halves of the last row; from sm one wrapping row as before. */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <input
          className="ui-input col-span-2 sm:w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('vault.search')}
          aria-label={t('vault.search')}
        />
        <Select
          label={t('vault.allTypes')}
          hideLabel
          clearable
          size="sm"
          value={typeFilter}
          onChange={setTypeFilter}
          placeholder={t('vault.allTypes')}
          options={VAULT_TYPES.map((value: VaultType) => ({
            value,
            label: t(`vault.type.${value}`),
          }))}
          className="col-span-2 sm:w-44"
        />
        {canEdit && (
          <div className="col-span-2 grid grid-cols-2 gap-2 sm:ml-auto sm:flex sm:items-center">
            <button
              type="button"
              className="ui-btn-secondary"
              onClick={() => setShowAudit((v) => !v)}
            >
              {showAudit ? t('vault.entriesToggle') : t('vault.auditToggle')}
            </button>
            <button type="button" className="ui-btn-primary" onClick={() => setCreating(true)}>
              <Plus size={15} /> {t('vault.add')}
            </button>
          </div>
        )}
      </div>

      {deleteMutation.isError && (
        <p className="mb-3 text-sm text-red-600">{t('vault.deleteFailed')}</p>
      )}

      {showAudit && canEdit ? (
        <AuditPanel projectId={project.id} />
      ) : data.length === 0 ? (
        <p className="text-sm text-gray-500">{t('vault.empty')}</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-500">{t('vault.emptyFiltered')}</p>
      ) : (
        <div className="space-y-6">
          {groups.map(([type, entries]) => (
            <section key={type}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t(`vault.type.${type}`)}
              </h2>
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                {entries.map((entry) => (
                  <EntryCard
                    key={entry.id}
                    projectId={project.id}
                    entry={entry}
                    canReveal={canReveal}
                    canEdit={canEdit}
                    onEdit={() => setEditingEntry(entry)}
                    onDelete={() => void handleDelete(entry)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {creating && (
        <VaultEntryModal
          projectId={project.id}
          canReveal={canReveal}
          onClose={() => setCreating(false)}
        />
      )}
      {editingEntry && (
        <VaultEntryModal
          projectId={project.id}
          entry={editingEntry}
          canReveal={canReveal}
          onClose={() => setEditingEntry(null)}
        />
      )}
    </div>
  );
}
